<script setup>
import { useVideoStore } from '~~/store/video'
import { useIsMobile } from '~~/composables/useIsMobile'
import { useYoutube } from '~/composables/useYoutube'
import getYoutubeID from 'get-youtube-id'

const { t } = useI18n()

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: null,
  },
  videoGtmClass: {
    type: String,
    default: null,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  youtube: {
    type: Boolean,
    default: false,
  },
  featured: {
    type: Boolean,
    default: false,
  },
  poster: {
    type: String,
    default: null,
  },
  posterMobile: {
    type: String,
    default: null,
  },
  posterTransform: {
    type: Object,
    default: null,
  },
})

const videoEl = ref(null)
const store = useVideoStore()
const isMobile = useIsMobile()

/**
 * Display
 */

const eyebrow = computed(() =>
  props.featured && props.isLivestream
    ? t('videoPlayer.live')
    : t('videoPlayer.watchingNow')
)

/**
 * Play and progress
 */
let player // for youtube & youtube livestreams

//To get start time if it's included into src url
const startTime = computed(() => {
  if (!props.src) return
  const params = new URLSearchParams(new URL(props.src).search)
  const startTimeValue = params.get('t')

  if (!startTimeValue || isNaN(startTimeValue)) return 0

  return Number(startTimeValue)
})

const duration = ref(0)
const currentTime = ref(0)
const progress = computed(() =>
  duration.value ? currentTime.value / duration.value : 1
)
const isPlaying = ref(false)
const hasPlayed = ref(false)
const hasAutoplayed = ref(false)
const isInView = ref(false)
const isYoutube = computed(() => props.youtube || props.featured)

function togglePlay() {
  if (isYoutube.value) {
    if (isPlaying.value) {
      player.pauseVideo()
    } else {
      player.playVideo()
    }
  } else {
    if (isPlaying.value) {
      videoEl.value?.pause()
    } else {
      videoEl.value?.play()
    }
  }
}

watchEffect(async () => {
  if (!videoEl.value) {
    return
  }

  // autoplay if this is the initial load and autoplay is specified
  if (props.autoplay && isInView.value && !hasAutoplayed.value) {
    const playPromise = videoEl.value?.play()
    playPromise
      .then(() => {
        hasAutoplayed.value = true
      })
      .catch(() => {
        console.log('cannot autoplay')
      })
    return
  }

  // when videoEl changes (when we switch to or from fullscreen) make sure it maintains currentTime and play state
  if (isTransitioning.value) {
    videoEl.value.currentTime = currentTime.value
    if (isPlaying.value) {
      await nextTick()
      videoEl.value?.play()
    }
    isTransitioning.value = false
  }
})

/**
 * Fullscreen controls and effects
 */
const isTransitioning = ref(false)
const isFullscreen = ref(false)

useHead({
  bodyAttrs: {
    class: { 'no-scroll': () => isFullscreen.value },
  },
})

async function toggleFullscreen() {
  destroyListeners()
  isTransitioning.value = true
  isFullscreen.value = !isFullscreen.value
}

/**
 * Volume controls
 */
const isMuted = computed(() => store.muted)
const volume = computed(() => store.volume)

watchEffect(() => {
  if (videoEl.value) {
    videoEl.value.volume = isMuted.value ? 0 : volume.value
  }
})

/**
 * Seeking
 */
const progressTrack = ref(null)

function seek(event) {
  const bounds = progressTrack.value.getBoundingClientRect()
  const seekProgress =
    (event.clientX - bounds.left) / (bounds.right - bounds.left)
  const progressClamped = Math.max(Math.min(seekProgress, 1), 0)

  if (isYoutube.value) {
    player?.seekTo(progressClamped * duration.value)
  } else {
    videoEl.value.currentTime = progressClamped * duration.value
  }
}

/**
 * Poster
 */
const videoID = computed(() => isYoutube.value && getYoutubeID(props.src))
const posterSrc = computed(
  () =>
    props.poster ||
    (isYoutube.value &&
      videoID.value &&
      `https://img.youtube.com/vi/${videoID.value}/hqdefault.jpg`)
)
const showPoster = computed(() =>
  isYoutube.value ? !isPlaying.value : props.poster && !hasPlayed.value
)
const posterStyle = computed(() =>
  props.posterTransform
    ? `transform: translateY(${props.posterTransform.translation}px) scale(${props.posterTransform.scale})`
    : null
)

/**
 * Video Events
 */
function onTimeUpdate(event) {
  currentTime.value = event.target.currentTime
}

function onDurationChange(event) {
  duration.value = event.target.duration
}

function onPlay() {
  console.log('onPlay')
  hasPlayed.value = true
  isPlaying.value = true
}

function onPause() {
  isPlaying.value = false
}

function initListeners() {
  videoEl.value.addEventListener('timeupdate', onTimeUpdate)
  videoEl.value.addEventListener('durationchange', onDurationChange)
  videoEl.value.addEventListener('play', onPlay)
  videoEl.value.addEventListener('pause', onPause)
}

function destroyListeners() {
  videoEl.value.removeEventListener('timeupdate', onTimeUpdate)
  videoEl.value.removeEventListener('durationchange', onDurationChange)
  videoEl.value.removeEventListener('play', onPlay)
  videoEl.value.removeEventListener('pause', onPause)
}

let observer
function initObserver() {
  observer = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting) {
      isInView.value = true
    } else {
      isInView.value = false
    }
  })
  observer.observe(videoEl.value)
}

watchEffect(async () => {
  if (videoEl.value && !isYoutube.value) {
    initListeners()
    initObserver()
  }
})

/**
 * Youtube integration
 */

const { youtube } = useYoutube()
function ytPlayerStateChange({ data: playerState }) {
  console.log('ytPlayerStateChange', playerState)
  switch (playerState) {
    case youtube.value.PlayerState.PLAYING:
      onPlay()
      break
    case youtube.value.PlayerState.ENDED:
    case youtube.value.PlayerState.PAUSED:
      onPause()
      break
  }
}

let shouldUpdate = true
function getTimeValues() {
  if (shouldUpdate) {
    requestAnimationFrame(getTimeValues)
  }

  if (player) {
    duration.value = player.getDuration()
    currentTime.value = player.getCurrentTime()
  }
}

onBeforeUnmount(() => {
  shouldUpdate = false
})

function setVolume({ volume, muted }) {
  if (volume !== undefined) {
    player?.setVolume(volume)
  }

  if (muted !== undefined) {
    if (muted) {
      player?.mute()
    } else {
      player?.unMute()
    }
  }
}

watchEffect(() => {
  setVolume({
    volume: store.volume,
    muted: store.muted,
  })
})

onBeforeUnmount(() => {
  // player?.removeEventListener('onReady', initYTEvents)
  player?.removeEventListener('onStateChange', ytPlayerStateChange)
})

watch(
  [isYoutube, youtube, videoEl, startTime],
  ([isYoutubeValue, youtubeValue, videoElValue, startTime]) => {
    if (isYoutubeValue && youtubeValue && videoElValue) {
      player = new youtubeValue.Player(videoElValue, {
        videoId: videoID.value,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          enablejsapi: 1,
          modestbranding: 1,
          origin: window.location.origin,
          playsinline: 1,
          start: startTime,
        },
        events: {
          onReady: () => {
            console.log('onReady')
            getTimeValues()
          },
          onStateChange: (event) => {
            console.log('onStateChange')
            ytPlayerStateChange(event)
          },
        },
      })

      console.log(player)
    }
  }
)
</script>

<template>
  <div v-if="inline" class="video-player__inline-container">
    <div
      class="video-player video-player--inline"
      :class="{
        'video-player--is-playing': isPlaying,
        'video-player--is-featured': featured,
      }"
    >
      <div
        v-if="isYoutube"
        class="video-player__video video-player__video--youtube"
        ref="videoEl"
      />
      <video
        v-else
        class="video-player__video"
        controlslist="nodownload"
        ref="videoEl"
        @click="togglePlay"
      >
        <source v-if="src" :src="src" type="video/mp4" />
      </video>

      <Transition>
        <button
          @click="togglePlay"
          v-if="showPoster"
          class="video-player__poster"
          :class="{
            'video-player__poster--will-transform': !!posterTransform,
            [videoGtmClass]: !!videoGtmClass,
          }"
          :style="posterStyle"
        >
          <picture class="video-player__poster-image">
            <source
              v-if="posterMobile"
              media="(max-width: 1023px)"
              :srcset="posterMobile"
            />
            <source media="(min-width: 1024px)" :srcset="posterSrc" />
            <img :src="posterSrc" class="video-player__poster-image" alt="Poster Image" />
          </picture>

          <div class="video-player__poster-button">
            <Icon name="play" class="video-player__poster-button-icon" />
          </div>
        </button>
      </Transition>

      <button class="video-player__overlay" @click="togglePlay" />

      <div v-show="isPlaying" class="video-player__controls">
        <VideoPlayButton @click="togglePlay" :is-playing="isPlaying" />
        <div class="video-player__controls-main">
          <div class="video-player__eyebrow text__eyebrow-small">
            <Icon class="video-player__eyebrow-icon" name="circle" />
            {{ eyebrow }}
          </div>
          <div class="video-player__title text__eyebrow-small">
            {{ title }}
          </div>
          <div class="video-player__progress">
            <button
              class="video-player__progress-track video-player__progress-track--seekable"
              ref="progressTrack"
              @mousedown="seek"
            >
              <div
                class="video-player__progress-inner"
                :style="{ width: `${progress * 100}%` }"
              />
            </button>
          </div>
        </div>
        <VideoVolumeControl class="video-player__volume" />
      </div>
    </div>
  </div>

  <div v-else-if="isFullscreen">
    <Teleport to="#fullscreen-video-container">
      <div
        class="video-player video-player--fullscreen"
        :class="{
          'video-player--is-playing': isPlaying,
          'video-player--is-featured': featured,
        }"
      >
        <video
          class="video-player__video"
          controlslist="nodownload"
          ref="videoEl"
        >
          <source v-if="src" :src="src" type="video/mp4" />
        </video>

        <div class="video-player__overlay" />

        <div class="video-player__controls">
          <VideoPlayButton @click="togglePlay" :is-playing="isPlaying" />
          <div class="video-player__controls-main">
            <div class="video-player__eyebrow text__eyebrow-small">
              <Icon class="video-player__eyebrow-icon" name="circle" />
              {{ eyebrow }}
            </div>
            <div class="video-player__title text__eyebrow-small">
              {{ title }}
            </div>
            <div class="video-player__progress">
              <button
                class="video-player__progress-track video-player__progress-track--seekable"
                ref="progressTrack"
                @mousedown="seek"
              >
                <div
                  class="video-player__progress-inner"
                  :style="{ width: `${progress * 100}%` }"
                />
              </button>
            </div>
          </div>
          <VideoVolumeControl class="video-player__volume" />
          <button class="video-player__fullscreen" @click="toggleFullscreen">
            <Icon name="fullscreen" class="video-player__fullscreen-icon" />
            <span class="u-visually-hidden">{{
              $t('videoPlayer.viewInline')
            }}</span>
          </button>
        </div>
      </div>
    </Teleport>
  </div>

  <div v-else class="video-player video-player--preview">
    <button @click="togglePlay" v-if="showPoster" class="video-player__poster">
      <img class="video-player__poster-image" :src="posterSrc" alt=""/>

      <div class="video-player__poster-button">
        <Icon name="play" class="video-player__poster-button-icon" />
      </div>
    </button>
    <video
      class="video-player__video"
      controlslist="nodownload"
      ref="videoEl"
      @click="togglePlay"
    >
      <source v-if="src" :src="src" type="video/mp4" />
    </video>

    <button class="video-player__fullscreen" @click="toggleFullscreen">
      <Icon name="fullscreen" class="video-player__fullscreen-icon" />
      <span class="u-visually-hidden">{{
        $t('videoPlayer.viewFullscreen')
      }}</span>
    </button>
    <div class="video-player__progress">
      <div class="video-player__progress-track">
        <div
          class="video-player__progress-inner"
          :style="{ width: `${progress * 100}%` }"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '~/styles/utils/variables';
@import '~/styles/utils/functions';
@import '~/styles/vendors/sass-mq/mq';

.video-player {
  position: relative;

  &__video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__eyebrow {
    margin-bottom: 3px;
  }
  &__eyebrow-icon {
    margin-right: 8px;

    .video-player--is-featured & {
      box-shadow: 0 0 40px 15px $brand-magenta;
      circle {
        fill: $brand-white;
      }
    }
  }

  &__inline-container {
    background: #000;
  }

  &__poster {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 25;

    &.v-leave-to,
    &.v-enter-from {
      opacity: 0;
      transform: scale(1.2);
    }
    &.v-leave-active,
    &.v-enter-active {
      transition: opacity 0.5s, transform 0.5s;
    }
  }

  &__poster-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__poster-button {
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: linear-gradient(110.59deg, #c80afb 7.52%, #2919a9 153.34%);
    box-shadow: 0px 0px 200px 29px $brand-magenta;

    position: absolute;
    top: calc(50% - 29px);
    left: calc(50% - 29px);
    z-index: 50;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__poster-button-icon {
    display: block;
    width: 22px;
    height: 22px;
  }

  &__fullscreen {
    display: block;
    height: 12px;
    width: 12px;

    &-icon {
      display: block;
    }

    @include mq($until: 'l') using ($until) {
      display: none;
    }
  }

  &__progress {
    width: 100%;
  }

  &__progress-track {
    width: 100%;
    height: 2px;
    background: rgba($brand-white, 0.2);
    position: relative;

    &--seekable {
      cursor: pointer;
    }
  }

  &__progress-inner {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(103.2deg, #d90afb -13.13%, #31afd4 93.9%);
    opacity: 0.9;
  }

  &--preview {
    .video-player {
      &__progress {
        position: absolute;
        bottom: 0;
        left: 0;
      }

      &__fullscreen {
        position: absolute;
        right: 27px;
        bottom: 27px;
      }
    }
  }

  &--fullscreen {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &--inline {
    position: relative;
  }
  &__video--youtube {
    aspect-ratio: 16/9;
    .page-home__featured-player & {
      aspect-ratio: auto;
      height: var(--real-height, 100vh);
    }
  }

  &--fullscreen,
  &--inline {
    .video-player {
      &__video {
        z-index: 10;
        &--youtube {
          // aspect-ratio: 16 / 9;
        }
      }
      &__overlay {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        z-index: 20;

        background: radial-gradient(
            117.57% 117.57% at 50% 50%,
            rgba(17, 9, 32, 0) 0%,
            rgba(17, 9, 32, 0.3) 100%
          ),
          linear-gradient(18.73deg, #0e061e 2.78%, rgba(14, 5, 31, 0) 47.69%);
      }
      &__controls {
        position: absolute;
        z-index: 30;
        width: 100%;
        padding-left: gs(0, 1, 'xxxs');
        padding-right: gs(0, 1, 'xxxs');
        left: 50%;
        bottom: gs(0, 1, 'xxxs');
        transform: translateX(-50%);

        @include mq($from: 'l') using ($from) {
          bottom: 56px;
          padding-left: gs(3, 3, 'l');
          padding-right: gs(3, 3, 'l');
        }

        display: flex;
        align-items: flex-end;

        > * {
          flex-shrink: 0;
          &:not(:first-child) {
            margin-left: 24px;
          }
        }
      }

      &__controls-main {
        flex-grow: 1;
      }

      &__volume {
        margin-bottom: 2px;
      }

      &__fullscreen {
        margin-bottom: 12px;
      }

      &__title {
        opacity: 0.7;
        margin-bottom: 5px;
      }

      &__progress {
        height: 38px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
